import { useEffect, useState } from "react";
import api, { formatTL, formatDateTime } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Wallet, TrendingUp, Banknote, Receipt, Percent } from "lucide-react";

const TYPE_COLORS = {
  teknisyen_borc: { sign: "+", color: "text-rose-700", label: "Tahsilat Borcu" },
  teknisyen_odeme: { sign: "−", color: "text-emerald-700", label: "Şirkete Ödendi" },
  teknisyen_prim: { sign: "•", color: "text-amber-700", label: "Prim Hakedişi" },
};

export default function MyBalance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.id) api.get(`/technicians/${user.id}/balance`).then(r => setData(r.data));
  }, [user?.id]);

  if (!data) return <div>Yükleniyor...</div>;

  const isOwing = data.balance > 0;

  return (
    <div className="space-y-4" data-testid="tech-balance">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Kasa</div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">Bakiyem</h1>
      </div>

      <Card className={`p-6 border shadow-none text-white ${isOwing ? "bg-gradient-to-br from-rose-700 to-rose-900 border-rose-800" : "bg-gradient-to-br from-emerald-700 to-emerald-900 border-emerald-800"}`}>
        <div className="text-xs uppercase tracking-wider text-white/70 font-bold">
          {isOwing ? "Şirkete Borcum" : "Borç Yok"}
        </div>
        <div className="font-display text-4xl font-bold mt-2">{formatTL(Math.abs(data.balance))}</div>
        <div className="mt-4 text-sm text-white/80">
          {isOwing
            ? "Bu tutarı şirkete teslim etmen gerekiyor."
            : "Tüm tahsilatların kapalı, harika iş!"}
        </div>
        {data.iban && (
          <div className="mt-3 pt-3 border-t border-white/10 text-xs">
            <div className="text-white/60 uppercase tracking-wider font-bold">Kayıtlı IBAN</div>
            <div className="font-mono text-white/90 mt-0.5">{data.iban}</div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Card className="p-3 border border-slate-200 shadow-none">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <TrendingUp className="h-3 w-3" /> Toplam Tahsilat Borcu
          </div>
          <div className="font-mono text-base font-bold text-rose-700 mt-1">{formatTL(data.total_debt)}</div>
        </Card>
        <Card className="p-3 border border-slate-200 shadow-none">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <Banknote className="h-3 w-3" /> Şirkete Ödediğim
          </div>
          <div className="font-mono text-base font-bold text-emerald-700 mt-1">{formatTL(data.total_paid)}</div>
        </Card>
        <Card className="p-3 border border-slate-200 shadow-none">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <Percent className="h-3 w-3" /> Toplam Prim Hakedişi
          </div>
          <div className="font-mono text-base font-bold text-amber-700 mt-1">{formatTL(data.total_commission)}</div>
        </Card>
        <Card className="p-3 border border-slate-200 shadow-none">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <Wallet className="h-3 w-3" /> Tamamlanan İş
          </div>
          <div className="font-display text-base font-bold text-slate-900 mt-1">{data.completed_jobs}</div>
        </Card>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="h-4 w-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Hareket Geçmişi</h3>
        </div>
        {data.transactions.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-6">Henüz hareket yok</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.transactions.map(t => {
              const c = TYPE_COLORS[t.type] || { sign: "", color: "text-slate-700", label: t.type };
              return (
                <div key={t.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.description || c.label}</div>
                    <div className="text-xs text-slate-500">{c.label} • {formatDateTime(t.date)}</div>
                  </div>
                  <div className={`font-mono font-bold ${c.color}`}>
                    {c.sign}{formatTL(t.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
